class HiyoIngredientsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ingredients-track]');
    this.items = [...this.querySelectorAll('[data-ingredients-item]')];
    this.previousButton = this.querySelector('[data-ingredients-previous]');
    this.nextButton = this.querySelector('[data-ingredients-next]');
    this.abortController = new AbortController();

    if (!this.track || this.items.length === 0) return;

    const { signal } = this.abortController;

    this.previousButton?.addEventListener('click', () => this.scrollByCard(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.scrollByCard(1), { signal });
    this.track.addEventListener('scroll', this.queueUpdate, { passive: true, signal });
    this.track.addEventListener('keydown', this.handleTrackKeydown, { signal });
    window.addEventListener('resize', this.queueUpdate, { passive: true, signal });
    this.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

    this.querySelectorAll('[data-ingredients-summary]').forEach((summary) => {
      summary.addEventListener('click', this.handleSummaryClick, { signal });
    });

    this.updateState();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    cancelAnimationFrame(this.updateFrame);
  }

  queueUpdate = () => {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.updateState());
  };

  getStep() {
    if (!this.track || this.items.length === 0) return 0;

    const itemWidth = this.items[0].getBoundingClientRect().width;
    const styles = getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;

    return itemWidth + gap;
  }

  scrollByCard(direction) {
    if (!this.track) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({
      left: this.getStep() * direction,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  handleTrackKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.scrollByCard(event.key === 'ArrowLeft' ? -1 : 1);
  };

  handleBlockSelect = (event) => {
    const item = event.target.closest('[data-ingredients-item]');
    if (!item || !this.track) return;

    item.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  handleSummaryClick = (event) => {
    const summary = event.currentTarget;
    const details = summary.closest('[data-ingredients-details]');
    if (!details || details.dataset.animating === 'true') return;

    event.preventDefault();

    const opening = !details.open;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      details.open = opening;
      summary.setAttribute('aria-expanded', String(opening));
      return;
    }

    details.dataset.animating = 'true';

    if (opening) details.open = true;

    const summaryHeight = summary.getBoundingClientRect().height;
    const collapsedHeight = summaryHeight + 24;
    const expandedHeight = details.scrollHeight;
    const startHeight = opening ? collapsedHeight : expandedHeight;
    const endHeight = opening ? expandedHeight : collapsedHeight;

    details.style.overflow = 'hidden';
    details.style.height = `${startHeight}px`;

    const animation = details.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      {
        duration: 280,
        easing: 'ease',
      }
    );

    animation.addEventListener(
      'finish',
      () => {
        details.open = opening;
        details.style.removeProperty('height');
        details.style.removeProperty('overflow');
        delete details.dataset.animating;
        summary.setAttribute('aria-expanded', String(opening));
      },
      { once: true }
    );
  };

  updateState() {
    if (!this.track || this.items.length === 0) return;

    const maxScrollLeft = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const currentScrollLeft = Math.max(0, this.track.scrollLeft);
    const edgeTolerance = 2;

    if (this.previousButton) {
      this.previousButton.disabled = currentScrollLeft <= edgeTolerance;
    }

    if (this.nextButton) {
      this.nextButton.disabled = currentScrollLeft >= maxScrollLeft - edgeTolerance;
    }

    const step = this.getStep();
    const selectedIndex = step > 0 ? Math.round(currentScrollLeft / step) : 0;
    const selectedItem = this.items[Math.min(this.items.length - 1, Math.max(0, selectedIndex))];

    this.items.forEach((item) => {
      item.classList.toggle('is-selected', item === selectedItem);
    });
  }
}

if (!customElements.get('hiyo-ingredients-carousel')) {
  customElements.define('hiyo-ingredients-carousel', HiyoIngredientsCarousel);
}
